import { useSwaggerlessContext } from 'Context/SwaggerlessContext';
import React, { useEffect, useState } from 'react';
import fr from 'facet-rest';
import { decodeQuery, addQuery } from 'Helpers/Query';
import { convertResponse } from 'Helpers/Plan';
import { getOptions } from 'Helpers/Meta';
import { displayErrorMessage, displaySuccess } from 'Helpers/FacetNotificationManager';
import usePrevious from 'Hooks/usePrevious';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import LoadingOverlay from 'Components/UI/LoadingOverlay/LoadingOverlay';
import { ParagraphTooltip } from 'Components/UI/ParagraphTooltip';
import { handleSendPlanMessage } from 'Components/AdvisorApp/Viewport/Household/Plan/Templates/Summary';
import ConfirmationDialog from 'Components/UI/ConfirmationDialog';
import { withHousehold } from 'Context/HouseholdContext';
import { makeStyles } from '@material-ui/core/styles';
import HelpIcon from '@material-ui/icons/Help';
import Typography from '@material-ui/core/Typography';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import { useLocation } from 'react-router';
import {
  currentFile as currentFileConst,
  files as filesConst,
} from 'Consts/Queries';
import modalConsts from 'Consts/ModalNames';
import ExternalPlanBuilderLink from './ExternalPlanBuilderLink';
import PlanListEntry from './PlanListEntry';
import './index.css';

const useStyles = makeStyles(theme => ({
  lists: {
    display: 'flex',
    flexDirection: 'row',
  },
  listsItem: {
    display: 'flex',
    flexDirection: 'column',
    width: '80%',
    padding: theme.spacing(0, 3),
  },
  listTitle: {
    alignSelf: 'center',
  },
  calculatorListEntry: {
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
    borderBottomColor: theme.palette.facet.border,
    display: 'flex',
    padding: theme.spacing(2, 0),
    justifyContent: 'space-between',
  },
  calculatorLink: {
    display: 'flex',
    fontSize: theme.typography.h2.fontSize,
    textAlign: 'left',
  },
  calculatorNote: {
    display: 'flex',
    fontSize: theme.typography.h6.fontSize,
    color: theme.palette.facet.darkGray,
    fontWeight: theme.typography.h6.fontWeight,
    textAlign: 'left',
    paddingTop: theme.spacing(0.75),
  },
  calculatorTooltipPadding: {
    paddingTop: theme.spacing(2.5),
  },
  calculatorTooltip: {
    display: 'flex',
    cursor: 'pointer',
    fill: theme.palette.facet.accentBlue,
  },
}));

const Home = () => {
  const { fetchMeta, getPlans } = useSwaggerlessContext();
  const classes = useStyles();

  const { householdResource } = useSimpleHousehold();
  const {
    rootNodeProps,
    logEvent,
    logEventSuccess,
    logEventFailureWithError,
    logResourceEvent,
  } = useEventTrackingLogEvent();

  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [externalPlanData, setExternalPlanData] = useState([]);
  const [
    resourcesExternalCalculators,
    setResourcesExternaCalculators,
  ] = useState([]);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    show: false,
    planId: null,
    planRemove: null,
  });
  const location = useLocation();
  const prevLocation = usePrevious(location);

  const getPlanIconSrc = (plan) => {
    const result = externalPlanData.find(
      item => item.id === plan.externalPlanType,
    );
    return result ? result.iconSrc : '';
  };
  const [uploadConfirmation, setUploadConfirmation] = useState({
    show: false,
    url: null,
  });

  const clearDeleteConfirmation = () => setDeleteConfirmation({
    show: false,
    planId: null,
    planRemove: null,
  });
  const clearUploadConfirmation = () => setUploadConfirmation({
    show: false,
    url: null,
  });

  useEffect(() => {
    let newExternalPlanData = [];

    fetchMeta('externalPlan', {})
      .then(({ data: planMeta }) => {
        const resultTypes = getOptions(
          planMeta.attributes.template,
          'type',
        ).filter(item => item.id !== 0);
        const resultIcons = getOptions(planMeta.attributes.template, 'icon');

        //  Zip these values together.
        const resultData = [];
        for (let i = 0; i < resultTypes.length; i++) {
          const { id } = resultTypes[i];
          resultData.push({
            id,
            value: resultTypes[i].value,
            iconSrc: resultIcons.find(item => item.id === id)?.value,
          });
        }
        newExternalPlanData = resultData;
      })
      .then(() => getPlans(householdResource.id()))
      .then(({ data: plansData }) => {
        //  Set these together for consistency.
        setPlans(plansData.map(plan => convertResponse(plan)));
        setExternalPlanData(newExternalPlanData);
      });
  }, [householdResource]);

  useEffect(() => {
    if (householdResource) {
      const params = decodeQuery(location.search);
      if (householdResource.id() !== +params.householdId) {
        window.location.reload();
      }
      householdResource
        .relationship('externalCalculators')
        .link('related')
        .Get()
        .then((coll) => {
          setResourcesExternaCalculators(coll.resources().list());
        });
    }
  }, [householdResource]);

  //  This refresh on location search change was added for a bugfix.
  //  It was then extended to ignore refreshing when modals are in the search.
  useEffect(() => {
    if (prevLocation
      && prevLocation.search !== location.search
      && !prevLocation.search.includes('modal')
      && !location.search.includes('modal')) {
      window.location.reload();
    }
  }, [location]);

  return (
    <div
      className="Home"
      {...rootNodeProps}
    >
      {loading && <LoadingOverlay />}
      {deleteConfirmation.show && (
        <ConfirmationDialog
          title="Delete Plan?"
          onConfirm={() => {
            setLoading(true);
            deleteConfirmation.planRemove(deleteConfirmation.planId)
              .then(() => {
                setPlans(plans.filter(plan => plan.id !== deleteConfirmation.planId));
                setLoading(false);
                clearDeleteConfirmation();
                logEventSuccess('Delete Plan', { planId: deleteConfirmation.planId });
              })
              .catch((err) => {
                displayErrorMessage(err, err.errors);
                clearDeleteConfirmation();
                setLoading(false);
                logEventFailureWithError('Delete Plan', err, { planId: deleteConfirmation.planId });
              });
          }}
          onCancel={() => clearDeleteConfirmation()}
        />
      )}
      {uploadConfirmation.show && (
        <ConfirmationDialog
          title={uploadConfirmation.sendPlanMessage ? 'Upload Plan to secure files and send in Messages?' : 'Upload Plan To Secure Files?'}
          onConfirm={() => {
            const eventName = 'Uploaded Plan to Secure Files';
            setLoading(true);

            //  Plans need to refactored to use collections (that'll be non trivial work).
            fr.http.Put(uploadConfirmation.url)
              .then((resp) => {
                return fr.http.Get(resp?.data[0]?.relationships?.secureFile?.links?.related);
              })
              .then(async (resp) => {
                if (!uploadConfirmation.sendPlanMessage) {
                  return resp;
                }
                const fileId = resp?.data[0]?.id;
                await handleSendPlanMessage(uploadConfirmation.planMessageRelationship, fileId);
                return resp;
              })
              .then((resp) => {
                displaySuccess(uploadConfirmation.sendPlanMessage ? 'Plan sent in Messages.' : `Upload plan to secure files: ${resp?.data[0]?.attributes?.name}`);
                clearUploadConfirmation();
                setLoading(false);

                //  Pop a modal previewing our uploaded file.
                const fileId = resp?.data[0]?.id;
                addQuery(
                  { [currentFileConst]: fileId },
                  { [filesConst]: fileId },
                  { [modalConsts.modal]: modalConsts.fileViewer },
                );
                logEventSuccess(eventName, { planUrl: uploadConfirmation.url });
              })
              .catch((err) => {
                displayErrorMessage(err, err.errors ? err.errors : 'Failed to upload plan to secure files.');
                clearUploadConfirmation();
                setLoading(false);
                logEventFailureWithError(eventName, err, { planUrl: uploadConfirmation.url });
              });
          }}
          onCancel={() => clearUploadConfirmation()}
        />
      )}
      <h2>Plan Builder</h2>
      <div className="builder">
        {externalPlanData.map(data => (
          <ExternalPlanBuilderLink
            key={`external-plan-${data.id}`}
            externalPlanType={data.id}
            externalPlanLabel={data.value}
            externalPlanIconSrc={data.iconSrc}
          />
        ))}
      </div>
      <div className={classes.lists}>
        <div className={classes.listsItem}>
          <Typography className={classes.listTitle} variant="h2">
            Existing Plans
          </Typography>
          <table>
            <tbody>
              {plans.map(plan => (
                <PlanListEntry
                  key={plan.id}
                  {...plan}
                  onDelete={(id, remove) => {
                    setDeleteConfirmation({
                      show: true,
                      planId: id,
                      planRemove: remove,
                    });
                    logEvent('Clicked Delete Plan', { planId: id });
                  }}
                  uploadSecureFile={(url, opts = {}) => {
                    const { sendPlanMessage } = opts;
                    setUploadConfirmation({
                      show: true,
                      url,
                      sendPlanMessage,
                      planMessageRelationship: plan.planMessageRelationship,
                    });
                    logEvent('Clicked Save To Files', { planUrl: url });
                  }}
                  iconSrcOverride={
                    plan.externalPlanType ? getPlanIconSrc(plan) : ''
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
        <div className={classes.listsItem}>
          <Typography className={classes.listTitle} variant="h2">
            External Calculators
          </Typography>
          <table>
            <tbody>
              {resourcesExternalCalculators.map(resource => (
                <tr
                  className={classes.calculatorListEntry}
                  key={resource.attr('name')}
                >
                  <td>
                    <a
                      onClick={() => logResourceEvent('Clicked External Resource link', resource)}
                      className={classes.calculatorLink}
                      href={resource
                        .relationship('externalLink')
                        .link('related')
                        .url()}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {resource.attr('name')}
                    </a>
                    <Typography className={classes.calculatorNote}>
                      {resource.attr('note')}
                    </Typography>
                  </td>
                  <td>
                    <div className={classes.calculatorTooltipPadding}>
                      <ParagraphTooltip
                        className={classes.calculatorTooltip}
                        title={resource.attr('description')}
                        placement="top-start"
                      >
                        <HelpIcon />
                      </ParagraphTooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

Home.propTypes = {
};

export default withHousehold(Home);
